import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import type { usePdfDocument } from '@hooks/usePdfDocument';

/**
 * Owns the current-page canvas over a loaded PDF: page state, the canvas
 * ref, and a render of the current page at `zoomPct` percent of its
 * original size. While `zoomPct` is null (not yet initialized) it only
 * measures the page and reports its natural size through `onNaturalSize`, so
 * the owner can pick a fit-based starting zoom. `renderedPage` is 0 until the
 * first page has painted — the skeleton's cue, and the DrawingLayer's
 * re-measure key on page flips.
 *
 * The canvas is tracked as STATE, not a ref: reading and drawing mount the
 * document under different parents, so switching modes hands us a new, blank
 * element. A ref would keep pointing at the old one and the page would vanish;
 * state re-runs the render against whatever canvas is currently on screen.
 */
export function usePdfPageCanvas(
  pdfDoc: ReturnType<typeof usePdfDocument>,
  zoomPct: number | null,
  onNaturalSize: (size: { width: number; height: number } | undefined) => void,
): {
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  canvasRef: (node: HTMLCanvasElement | null) => void;
  renderedPage: number;
} {
  const [page, setPage] = useState(1);
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const [renderedPage, setRenderedPage] = useState(0);

  const { pdf, renderPage } = pdfDoc;
  useEffect(() => {
    if (!pdf || !canvas) return;
    // A render outlives the effect that started it, so a superseded one must
    // neither report its size nor surface its rejection (pdf.js rejects
    // in-flight renders when the document is destroyed).
    let superseded = false;
    renderPage(page, canvas, zoomPct === null ? null : zoomPct / 100)
      .then((natural) => {
        if (superseded) return;
        onNaturalSize(natural);
        if (zoomPct !== null) setRenderedPage(page);
      })
      .catch((err: unknown) => {
        if (!superseded) console.error('pdf page render failed', err);
      });
    return () => { superseded = true; };
    // renderPage/onNaturalSize are stable per pdf; re-render on page flips,
    // zoom changes, new documents — and a new canvas element.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdf, canvas, page, zoomPct]);

  return { page, setPage, canvasRef: setCanvas, renderedPage };
}
